import { useState } from 'react';

import { faGear } from '@fortawesome/free-solid-svg-icons';
import { Trans } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import { useAtom } from 'jotai';

import { VideaTypography } from '@/common-components/elements/VideaTypography/VideaTypography';
import { ToolbarButton } from '@/modules/annotator/imageToolbar/ToolbarButton';
import type { AdjustmentVersion } from '@/react-query/useGetSharpenedImages';
import { usePreferences } from '@/react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import type { AdjustmentType } from './atoms';
import {
  selectedAdjustmentTypeAtom,
  selectedImageIdAtom,
  selectedPatientIdAtom,
  selectedVisitAtom,
} from './atoms';
import { FilterComparisonPreview } from './FilterComparisonPreview';
import { FilterSelectionModalFooter } from './FilterSelectionModalFooter';
import { adjustmentVersionLabel } from './helpers';
import { ImageCarousel } from './ImageCarousel';
import { VisitSelector } from './VisitSelector';

const AdjustmentTypeButton = ({
  selectedAdjustmentType,
  adjustmentType,
  adjustmentVersionPreference,
  onClick,
}: {
  selectedAdjustmentType: AdjustmentType;
  adjustmentType: AdjustmentType;
  adjustmentVersionPreference: AdjustmentVersion | null;
  onClick: () => void | undefined;
}) => {
  return (
    <button
      onClick={onClick}
      disabled={selectedAdjustmentType === adjustmentType}
      className="vai-h-full vai-text-black vai-border-zinc-200 vai-border-[1px] vai-rounded-md vai-p-2 disabled:vai-bg-zinc-50 hover:vai-bg-zinc-100"
    >
      <div className="vai-flex vai-flex-col vai-gap-2 vai-items-start vai-justify-start vai-w-48">
        <span className="vai-text-md vai-font-medium">{adjustmentType}</span>
        <span className="vai-text-sm">
          <Trans>Current Setting:</Trans>{' '}
          {adjustmentVersionLabel(adjustmentVersionPreference).label}
        </span>
      </div>
    </button>
  );
};

interface ImageFilterSelectionProps {
  isInImageToolbar?: boolean;
}
export const ImageFilterSelection = ({
  isInImageToolbar = false,
}: ImageFilterSelectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useAtom(selectedVisitAtom);
  const [selectedPatientId, setSelectedPatientId] = useAtom(
    selectedPatientIdAtom,
  );
  const [selectedImageId, setSelectedImageId] = useAtom(selectedImageIdAtom);
  const [selectedAdjustmentType, setSelectedAdjustmentType] = useAtom(
    selectedAdjustmentTypeAtom,
  );

  const { trackEvent } = useTrackEvent();

  const { data: preferences } = usePreferences();

  const onClose = () => {
    setIsOpen(false);
    setSelectedVisit(undefined);
    setSelectedPatientId(undefined);
    setSelectedImageId(undefined);
    setSelectedAdjustmentType('HD1');
  };

  const handleAdjustmentTypeChange = (adjustmentType: AdjustmentType) => {
    if (
      (selectedAdjustmentType !== 'Pano' && adjustmentType === 'Pano') ||
      (selectedAdjustmentType === 'Pano' && adjustmentType !== 'Pano')
    ) {
      setSelectedVisit(undefined);
      setSelectedPatientId(undefined);
      setSelectedImageId(undefined);
    }
    setSelectedAdjustmentType(adjustmentType);
  };

  return (
    <>
      {isInImageToolbar ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <ToolbarButton
                icon={faGear}
                isActive={isOpen}
                onClick={() => {
                  setIsOpen(true);
                  trackEvent(
                    mixpanelEvents.sharpeningFilterSelectionModalOpen,
                    {
                      source: 'imageToolbar',
                    },
                  );
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent
            variant="dark"
            side="top"
            sideOffset={8}
            alignOffset={-4}
          >
            <p>
              <Trans>Configure Sharpening Filter</Trans>
            </p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button
          variant="outline"
          onClick={() => {
            setIsOpen(true);
            trackEvent(mixpanelEvents.sharpeningFilterSelectionModalOpen, {
              source: 'practicePreferencesMenu',
            });
          }}
        >
          <Trans>Configure Sharpening Filter</Trans>
        </Button>
      )}

      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="vai-max-w-6xl vai-max-h-[95vh] vai-overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <Trans>Image Sharpening Configuration</Trans>
            </DialogTitle>
          </DialogHeader>

          <div className="vai-flex vai-flex-col vai-gap-4 vai-w-full">
            {/* Instructions */}
            <div className="vai-flex vai-flex-col vai-gap-2">
              <div className="vai-flex vai-items-center vai-gap-2 vai-text-gray-600 vai-text-sm">
                <Trans>
                  Select a recent visit, choose an image, and compare different
                  sharpening filters side-by-side to determine your preferred
                  setting.
                </Trans>
              </div>
              <div className="vai-p-3 vai-rounded-md vai-bg-orange-50 vai-border vai-border-orange-200 vai-mt-2">
                <div className="vai-text-orange-800 vai-text-sm">
                  <Trans>
                    Note: This update will only affect images taken starting now
                    and will not automatically rerun all historical images. You
                    can manually rerun individual images if needed.
                  </Trans>
                </div>
              </div>
            </div>

            <div className="vai-flex vai-flex-col vai-gap-3">
              <VideaTypography variant="labelSmall">
                <Trans>Step 1: Select an Adjustment Type to Edit</Trans>
              </VideaTypography>
              <div className="vai-flex vai-flex-row vai-gap-4 vai-my-4">
                <div className="vai-flex vai-flex-col vai-gap-2">
                  <span className="vai-text-sm vai-font-medium">
                    <Trans>BITEWING / PERIAPICAL</Trans>
                  </span>
                  <div className="vai-flex vai-flex-row vai-gap-2">
                    <AdjustmentTypeButton
                      selectedAdjustmentType={selectedAdjustmentType}
                      adjustmentType="HD1"
                      adjustmentVersionPreference={
                        preferences?.adjustmentVersion as AdjustmentVersion | null
                      }
                      onClick={() => handleAdjustmentTypeChange('HD1')}
                    />
                    {preferences?.enableSharpeningHd2Selection && (
                      <AdjustmentTypeButton
                        selectedAdjustmentType={selectedAdjustmentType}
                        adjustmentType="HD2"
                        adjustmentVersionPreference={
                          preferences?.adjustmentVersionHD2 as AdjustmentVersion | null
                        }
                        onClick={() => handleAdjustmentTypeChange('HD2')}
                      />
                    )}
                  </div>
                </div>
                {preferences?.enablePanoAdjustmentSelection &&
                  !preferences?.disableAdjustmentForPanos && (
                    <div className="vai-flex vai-flex-col vai-gap-2">
                      <span className="vai-text-sm vai-font-medium">
                        <Trans>PANORAMIC</Trans>
                      </span>
                      <div className="vai-flex vai-flex-row vai-gap-2">
                        <AdjustmentTypeButton
                          selectedAdjustmentType={selectedAdjustmentType}
                          adjustmentType="Pano"
                          adjustmentVersionPreference={
                            preferences?.panoAdjustmentVersion as AdjustmentVersion | null
                          }
                          onClick={() => handleAdjustmentTypeChange('Pano')}
                        />
                      </div>
                    </div>
                  )}
              </div>
            </div>

            <div className="vai-flex vai-flex-col vai-gap-3">
              <VideaTypography variant="labelSmall">
                <Trans>Step 2: Select a Recent Visit</Trans>
              </VideaTypography>
              <VisitSelector />
            </div>

            {selectedVisit && selectedPatientId && (
              <div className="vai-flex vai-flex-col vai-gap-3">
                <div>
                  <Trans>Step 3: Select an Image</Trans>
                </div>
                <ImageCarousel />
              </div>
            )}

            {selectedImageId && selectedVisit && selectedPatientId && (
              <div className="vai-flex vai-flex-col vai-gap-3">
                <div>
                  <Trans>Step 4: Compare Filters Side-by-Side</Trans>
                </div>
                <FilterComparisonPreview />
              </div>
            )}

            <FilterSelectionModalFooter onCancel={onClose} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
